import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography
} from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import {
  useRemoteFetchFleetLogbook,
  VehicleTripLog
} from "../../../hooks/Remote/Fleet/useRemoteFetchFleetLogbook";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { EmptyTableState } from "../EmptyTableState.component";
import { LogbookAttachmentLinks } from "./LogbookDialog.partial";
import { escapeCsvField } from "./functions/escapeCsvField.function";
import { getDisplayTripReason } from "./functions/getDisplayTripReason.function";

export type LogbookDialogProps = {
  vehicleId: number | null;
  vehicleName: string;
  licensePlate?: string;
  onClose: () => void;
};

/**
 * Converts logbook data to CSV format
 */
function generateCsvFromLogbook(
  data: VehicleTripLog[],
  vehicleName: string,
  licensePlate: string,
  t: (key: string) => string,
  getDriverName: (driverId: string) => string
): string {
  const headers = [
    t("Trip ID"),
    t("Vehicle"),
    t("LicensePlate"),
    t("Driver Name"),
    t("Trip Reason"),
    t("Check-out Time"),
    t("Check-in Time"),
    t("_Odometer(out)"),
    t("_Odometer(in)"),
    t("Distance (km)"),
    t("_Notes(out)"),
    t("_Notes(in)")
  ];

  const rows = data.map(log => {
    const distance =
      log.checkInOdometer != null && log.checkOutOdometer != null
        ? log.checkInOdometer - log.checkOutOdometer
        : "";
    const checkOutTime = log.checkOutTime
      ? DateTime.fromISO(log.checkOutTime).toFormat("yyyy-MM-dd HH:mm:ss")
      : "";
    const checkInTime = log.checkInTime
      ? DateTime.fromISO(log.checkInTime).toFormat("yyyy-MM-dd HH:mm:ss")
      : "";

    return [
      log.id,
      vehicleName,
      licensePlate,
      getDriverName(log.driverId),
      getDisplayTripReason(log.tripReason, t("_logbook_noReasonProvided")),
      checkOutTime,
      checkInTime,
      log.checkOutOdometer,
      log.checkInOdometer ?? "",
      distance,
      log.checkOutNotes ?? "",
      log.checkInNotes ?? ""
    ].map(escapeCsvField);
  });

  return [headers.map(escapeCsvField).join(","), ...rows.map(row => row.join(","))].join("\n");
}

/**
 * Triggers download of CSV file
 */
function downloadCsv(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function LogbookDialog({
  vehicleId,
  vehicleName,
  licensePlate = "",
  onClose
}: Readonly<LogbookDialogProps>) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { dateFormat, timeFormat } = useTimeFormat();
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;

  const numberLocale = i18n.language === "de" ? "de-DE" : "en-US";
  const open = vehicleId != null;

  const [isExporting, setIsExporting] = useState(false);

  const { data: logbookData, isLoading, error } = useRemoteFetchFleetLogbook(vehicleId);
  const { data: users } = useRemoteFetchUserInfoForAllUsers();

  const handleExportCsv = async () => {
    if (!logbookData || logbookData.length === 0) return;

    setIsExporting(true);

    try {
      await Promise.resolve(); // yield so React can show "Exporting…" state
      const csvContent = generateCsvFromLogbook(
        logbookData,
        vehicleName,
        licensePlate,
        t,
        getDriverName
      );
      const safeName = vehicleName.replaceAll(/[^a-zA-Z0-9-_]/g, "_");
      downloadCsv(csvContent, `Logbook-${safeName}.csv`);
    } catch {
      enqueueSnackbar(t("Failed to export CSV"), { variant: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  const formatDateTime = (isoString: string | null) => {
    if (!isoString) return "-";
    const dt = DateTime.fromISO(isoString);
    return dt.isValid ? dt.toFormat(dateTimeFormat) : "-";
  };

  const formatOdometer = (value: number | null | undefined) => {
    if (value == null) return "-";
    return value.toLocaleString(numberLocale);
  };

  const getDriverName = (driverId: string) => {
    const user = users?.find(u => u.userId === driverId);
    if (!user) return driverId;
    return `${user.firstName} ${user.surname}`;
  };

  /** Renders the dialog content */
  const renderDialogContent = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={2}>
          <Typography color="error">{t("Failed to load logbook data")}</Typography>
        </Box>
      );
    }

    if (!logbookData || logbookData.length === 0) {
      return <EmptyTableState message={t("No logbook entries found")} />;
    }

    return (
      <TableContainer
        data-testid="logbook-dialog-table-container"
        component={Paper}
        sx={{ maxHeight: 600 }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>{t("Check-out Time")}</TableCell>
              <TableCell rowSpan={2}>{t("Check-in Time")}</TableCell>
              <TableCell rowSpan={2}>{t("Driver Name")}</TableCell>
              <TableCell rowSpan={2}>{t("Trip Reason")}</TableCell>
              <TableCell colSpan={2} align="center">
                {t("Odometer")}
              </TableCell>
              <TableCell rowSpan={2}>{t("Distance")}</TableCell>
              <TableCell colSpan={2} align="center">
                {t("Notes")}
              </TableCell>
              <TableCell rowSpan={2}>{t("Attachments")}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("_Odometer(out)")}</TableCell>
              <TableCell>{t("_Odometer(in)")}</TableCell>
              <TableCell>{t("_Notes(out)")}</TableCell>
              <TableCell>{t("_Notes(in)")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {logbookData.map(log => {
              const distance =
                log.checkInOdometer != null && log.checkOutOdometer != null
                  ? log.checkInOdometer - log.checkOutOdometer
                  : null;

              return (
                <TableRow key={log.id}>
                  <TableCell>{formatDateTime(log.checkOutTime)}</TableCell>
                  <TableCell>{formatDateTime(log.checkInTime)}</TableCell>
                  <TableCell>{getDriverName(log.driverId)}</TableCell>
                  <TableCell>
                    {getDisplayTripReason(log.tripReason, t("_logbook_noReasonProvided"))}
                  </TableCell>
                  <TableCell>{formatOdometer(log.checkOutOdometer)}</TableCell>
                  <TableCell>{formatOdometer(log.checkInOdometer)}</TableCell>
                  <TableCell>
                    {distance === null ? "-" : `${formatOdometer(distance)} km`}
                  </TableCell>
                  <TableCell>{log.checkOutNotes ?? "-"}</TableCell>
                  <TableCell>{log.checkInNotes ?? "-"}</TableCell>
                  <TableCell>
                    <LogbookAttachmentLinks log={log} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <DialogTitle>
        {t("Logbook")} – {vehicleName}
        {licensePlate ? ` (${licensePlate})` : ""}
      </DialogTitle>
      <DialogContent>{renderDialogContent()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" data-testid="logbook-dialog-close-btn">
          {t("Close")}
        </Button>

        <Button
          startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={handleExportCsv}
          disabled={isExporting || !logbookData || logbookData.length === 0}
          data-testid="logbook-export-csv-btn"
        >
          {isExporting ? t("Exporting…") : t("Export as CSV")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
